import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Funnel } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

export const INVENTORY_RECORD_SOURCE_TYPE_OPTIONS = [
  {
    value: "Purchase_From_Vendor",
    labelKey: "inventory.purchaseFromVendor",
  },
  {
    value: "Warranty_Replacement_From_Vendor",
    labelKey: "inventory.warrantyReplacementFromVendor",
  },
  {
    value: "Defective_Replacement_From_Vendor",
    labelKey: "inventory.defectiveReplacementFromVendor",
  },
  {
    value: "Excess_Return_From_Customer",
    labelKey: "inventory.excessReturnFromCustomer",
  },
  {
    value: "Warranty_Replacement_From_Customer",
    labelKey: "inventory.warrantyReplacementFromCustomer",
  },
  {
    value: "Defective_Replacement_From_Customer",
    labelKey: "inventory.defectiveReplacementFromCustomer",
  },
];

export const EMPTY_INVENTORY_RECORD_FILTERS = {
  sourceType: "All",
  batchNumber: "",
  productId: "All",
  warehouseLocationId: "All",
};

export function InventoryRecordFiltersModal({
  open,
  onOpenChange,
  appliedFilters,
  onApply,
  productOptions = [],
  warehouseOptions = [],
  loadingProducts = false,
  loadingMoreProducts = false,
  loadingWarehouses = false,
  loadingMoreWarehouses = false,
  onProductDropdownOpen,
  onWarehouseDropdownOpen,
  onProductListScroll,
  onWarehouseListScroll,
}) {
  const { t } = useTranslation();
  const [tempFilters, setTempFilters] = useState({
    ...EMPTY_INVENTORY_RECORD_FILTERS,
  });

  useEffect(() => {
    if (open) {
      setTempFilters({
        sourceType: appliedFilters.sourceType || "All",
        batchNumber: appliedFilters.batchNumber || "",
        productId: appliedFilters.productId || "All",
        warehouseLocationId: appliedFilters.warehouseLocationId || "All",
      });
    }
  }, [open, appliedFilters]);

  const handleChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApply({
      ...tempFilters,
      batchNumber: String(tempFilters.batchNumber || "").trim(),
    });
    onOpenChange(false);
  };

  const handleBatchKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleApply();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[450px]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-5 font-sans focus:outline-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Funnel className="h-4.5 w-4.5 text-primary" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("formActions.filterInventoryRecords")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden">
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">
                {t("inventory.sourceType")}
              </label>
              <Select
                value={tempFilters.sourceType}
                onValueChange={(val) => handleChange("sourceType", val)}
              >
                <SelectTrigger className="h-10 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:ring-accent w-full">
                  <SelectValue placeholder={t("inventory.allSourceTypes")} />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="All">
                    {t("inventory.allSourceTypes")}
                  </SelectItem>
                  {INVENTORY_RECORD_SOURCE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">
                {t("inventory.batchNumber")}
              </label>
              <input
                type="text"
                value={tempFilters.batchNumber}
                onChange={(e) => handleChange("batchNumber", e.target.value)}
                onKeyDown={handleBatchKeyDown}
                placeholder={t("inventory.batchNumberEnterHint")}
                className="h-10 w-full px-3 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:outline-hidden focus:ring-2 focus:ring-accent/20 focus:border-accent"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">
                {t("inventory.product")}
              </label>
              <Select
                value={tempFilters.productId}
                onValueChange={(val) => handleChange("productId", val)}
                onOpenChange={onProductDropdownOpen}
              >
                <SelectTrigger className="h-10 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:ring-accent w-full">
                  <SelectValue placeholder={t("inventory.allProducts")} />
                </SelectTrigger>
                <SelectContent
                  className="z-[100]"
                  viewportClassName="h-auto max-h-60 overflow-y-auto"
                  onViewportScroll={onProductListScroll}
                  loading={loadingProducts}
                >
                  <SelectItem value="All">{t("inventory.allProducts")}</SelectItem>
                  {productOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                  {loadingProducts || loadingMoreProducts ? (
                    <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                      {t("common.loading")}
                    </div>
                  ) : null}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">
                {t("inventory.warehouse")}
              </label>
              <Select
                value={tempFilters.warehouseLocationId}
                onValueChange={(val) =>
                  handleChange("warehouseLocationId", val)
                }
                onOpenChange={onWarehouseDropdownOpen}
              >
                <SelectTrigger className="h-10 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:ring-accent w-full">
                  <SelectValue placeholder={t("inventory.allWarehouses")} />
                </SelectTrigger>
                <SelectContent
                  className="z-[100]"
                  viewportClassName="h-auto max-h-60 overflow-y-auto"
                  onViewportScroll={onWarehouseListScroll}
                  loading={loadingWarehouses}
                >
                  <SelectItem value="All">
                    {t("inventory.allWarehouses")}
                  </SelectItem>
                  {warehouseOptions.map((w) => {
                    const value =
                      typeof w === "object" ? String(w.value) : String(w);
                    const label =
                      typeof w === "object" ? w.label || w.value : w;
                    return (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    );
                  })}
                  {loadingWarehouses || loadingMoreWarehouses ? (
                    <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                      {t("common.loading")}
                    </div>
                  ) : null}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-(--color-text-border)/50 shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
            >
              {t("formActions.cancel")}
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="h-10 px-5 rounded-xl bg-primary text-white font-bold text-xs hover:bg-primary/90 transition cursor-pointer shadow-sm"
            >
              {t("formActions.applyFilters")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
